import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Fragment, useMemo, useEffect, useRef, useState } from 'react';

import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';
import { IconButton } from '../buttons/IconButton';
import { OptionDeviceDeviceControls } from '../forms/select/OptionDeviceDeviceControls';
import { Select } from '../forms/select/Select';
import { SelectPreviewDeviceDeviceControls } from '../forms/select/SelectPreviewDeviceDeviceControls';
import { Tabs } from '../tabs/Tabs';

import { DeviceUtils } from '../../store/devices/DeviceUtils';
import { HomeyUtils } from '../../store/HomeyUtils.ts';
import { userMeDraftStore } from '../../store/users/UserMeDraftStore.ts';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useDevice } from '../../store/devices/useDevices.ts';
import { useLogsForOwnerUri } from '../../store/insights/useLogs.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';
import { useUserMe } from '../../store/users/useUserMe.ts';

import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

import { Battery } from './battery/Battery';
import { ButtonGroup } from './button/ButtonGroup';
import { Camera } from './camera/Camera';
import { Color } from './color/Color';
import { ControlsBackground } from './ControlsBackground';
import { ControlsComponentTab } from './ControlsComponentTab';
import { ControlsComponentTabPanel } from './ControlsComponentTabPanel';
import { ControlsMessage } from './ControlsMessage';
import { Media } from './media/Media';
import { Picker } from './picker/Picker';
import { SensorGroup } from './sensor/SensorGroup';
import { SliderAdapter } from './slider/SliderAdapter';
import { Ternary } from './ternary/Ternary';
import { Thermostat } from './thermostat/Thermostat';
import { Timeline } from './timeline/Timeline';
import { Toggle } from './toggle/Toggle';

export function Controls(props) {
  const { i18n } = useI18n();

  const { api, homey } = useApi();
  const { userMe } = useUserMe();
  const { device: sourceDevice, deviceInstance: sourceDeviceInstance } = useDevice({
    deviceId: props.deviceId,
  });
  const sourceDeviceId = props.deviceId ?? '';
  const [deviceSelection, setDeviceSelection] = useState({
    sourceDeviceId: sourceDeviceId,
    selectedDeviceId: sourceDeviceId,
  });
  const isSelectionForSourceDevice = deviceSelection.sourceDeviceId === sourceDeviceId;
  const selectedDeviceId = isSelectionForSourceDevice
    ? deviceSelection.selectedDeviceId
    : sourceDeviceId;
  const { device: selectedDevice } = useDevice({
    deviceId: selectedDeviceId,
  });
  const { logs } = useLogsForOwnerUri({
    ownerUri: `homey:device:${selectedDeviceId}`,
  });

  useEffect(() => {
    setDeviceSelection((currentSelection) => {
      if (currentSelection.sourceDeviceId === sourceDeviceId) {
        return currentSelection;
      }

      return {
        sourceDeviceId: sourceDeviceId,
        selectedDeviceId: sourceDeviceId,
      };
    });
  }, [sourceDeviceId]);

  const hasVideoStreaming = HomeyUtils.getHasVideoStreaming(homey);

  const { components, componentsStartAt } = useComponents({
    device: selectedDevice,
    logs: logs,
    hasVideoStreaming,
  });

  const initialTabKey = components?.[componentsStartAt]?.id ?? null;
  const [tabSelection, setTabSelection] = useState({
    deviceId: null,
    selectedTabKey: null,
  });
  const isTabSelectionForSelectedDevice = tabSelection.deviceId === selectedDeviceId;
  const isSelectedTabAvailable =
    components?.some((component) => {
      return component.id === tabSelection.selectedTabKey;
    }) ?? false;

  let selectedTabKey = initialTabKey;

  if (isTabSelectionForSelectedDevice && isSelectedTabAvailable) {
    selectedTabKey = tabSelection.selectedTabKey;
  }

  const pendingRef = useRef({
    favorite: null,
  });

  const isFavoriteDevice = DeviceUtils.isFavoriteDevice(selectedDevice, userMe);

  const instanceRef = useRef({
    prevWarningMessage: null,
    prevDeviceId: null,
  });
  Object.assign(instanceRef.current, { onClosePress: props.onClosePress });

  // When the device disappears.
  useEffect(() => {
    function listener() {
      instanceRef.current.onClosePress();
    }

    if (sourceDeviceInstance != null) {
      sourceDeviceInstance.on('delete', listener);
    }

    return function () {
      if (sourceDeviceInstance != null) {
        sourceDeviceInstance.off('delete', listener);
      }
    };
  }, [sourceDeviceInstance]);

  // When the warning message changes.
  useEffect(() => {
    if (sourceDevice?.warningMessage == null) {
      instanceRef.current.prevWarningMessage = null;
      return;
    }

    if (instanceRef.current.prevWarningMessage === sourceDevice.warningMessage) {
      return;
    }

    instanceRef.current.prevWarningMessage = sourceDevice.warningMessage;

    ToastManager.add({
      type: ToastManager.TYPES.WARNING,
      message: sourceDevice.warningMessage,
    });
  }, [sourceDevice?.warningMessage]);

  // When the device changes.
  useEffect(() => {
    if (props.deviceId !== instanceRef.current.prevDeviceId) {
      instanceRef.current.prevDeviceId = props.deviceId;
      instanceRef.current.prevWarningMessage = null;
    }
  }, [props.deviceId]);

  function handleFavoritePress() {
    (async () => {
      const favoriteName = `**${selectedDevice.name}**`;

      if (pendingRef.current.favorite === true) {
        ToastManager.add({
          message: 'Request still pending, please wait...',
        });
        return;
      }

      if (isFavoriteDevice) {
        try {
          pendingRef.current.favorite = true;

          const nextUserMe = {
            ...userMe,
            properties: {
              ...userMe.properties,
              favoriteDevices: (userMe.properties.favoriteDevices ?? []).filter(
                (id) => id !== selectedDevice.id,
              ),
            },
          };
          userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

          await api.users.updateUserMeProperties({
            id: 'favoriteDevices',
            value: nextUserMe.properties.favoriteDevices,
          });

          ToastManager.add({
            icon: glyph.core.heartOutline,
            iconColor: theme.color.icon_light,
            message: i18n.messageFormatter(`device.removeFavorite`, {
              name: favoriteName,
            }),
          });
          userMeDraftStore.setUserMeDraftDone();
        } catch (error) {
          userMeDraftStore.setUserMeDraftFailed();
          ToastManager.handleError(error);
        } finally {
          pendingRef.current.favorite = false;
        }

        return;
      }

      try {
        pendingRef.current.favorite = true;

        const nextUserMe = {
          ...userMe,
          properties: {
            ...userMe.properties,
            favoriteDevices: [...(userMe.properties.favoriteDevices ?? []), selectedDevice.id],
          },
        };
        userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

        await api.users.updateUserMeProperties({
          id: 'favoriteDevices',
          value: nextUserMe.properties.favoriteDevices,
        });

        ToastManager.add({
          icon: glyph.core.heartFill,
          iconColor: theme.color.special_favorite,
          message: i18n.messageFormatter(`device.addFavorite`, {
            name: favoriteName,
          }),
        });
        userMeDraftStore.setUserMeDraftDone();
      } catch (error) {
        userMeDraftStore.setUserMeDraftFailed();
        ToastManager.handleError(error);
      } finally {
        pendingRef.current.favorite = false;
      }
    })().catch(ToastManager.handleError);
  }

  function handleSettingsPress() {
    RouteManager.toDeviceSettings(selectedDeviceId);
  }

  function handleTabSelectionChange(key) {
    setTabSelection({
      deviceId: selectedDeviceId,
      selectedTabKey: key,
    });
  }

  const options = sourceDevice?.settings.deviceIds
    ? [
        { id: sourceDevice.id, textValue: sourceDevice.name },
        ...sourceDevice.settings.deviceIds.map((deviceId) => {
          return {
            id: deviceId,
            textValue: deviceId,
          };
        }),
      ]
    : sourceDevice != null
      ? [{ id: sourceDevice.id, textValue: sourceDevice.name }]
      : [];

  return (
    <>
      <ControlsBackground resetId={selectedDevice?.id} />

      <AnimatePresence exitBeforeEnter={true}>
        <Controls.Root
          aria-hidden={props.deviceId == null}
          key={props.deviceId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Controls.Header>
            <Controls.Actions>
              <Controls.IconButtonWrapper>
                <IconButton
                  url={glyph.core.cross}
                  color={theme.color.white_o_50}
                  size={theme.icon.size_medium}
                  isDisabled={props.deviceId == null}
                  onPress={props.onClosePress}
                />
              </Controls.IconButtonWrapper>
            </Controls.Actions>

            <Select
              isDisabled={sourceDevice?.driverId !== 'homey:virtualdrivergroup:driver'}
              previewComponent={SelectPreviewDeviceDeviceControls}
              optionComponent={OptionDeviceDeviceControls}
              orientation="vertical"
              overlayStyle={{ width: '320px' }}
              name="device"
              aria-label="Device"
              options={options}
              value={selectedDeviceId}
              onChange={(value) => {
                setDeviceSelection({
                  sourceDeviceId: sourceDeviceId,
                  selectedDeviceId: value,
                });
              }}
            />

            <Controls.Actions>
              <Controls.IconButtonWrapper>
                <IconButton
                  url={isFavoriteDevice ? glyph.core.heartFill : glyph.core.heartOutline}
                  size={theme.icon.size_medium}
                  color={theme.color.white_o_50}
                  isDisabled={props.deviceId == null}
                  onPress={handleFavoritePress}
                />
              </Controls.IconButtonWrapper>

              <Controls.IconButtonWrapper>
                <IconButton
                  url={glyph.system.settings}
                  color={theme.color.white_o_50}
                  size={theme.icon.size_medium}
                  isDisabled={props.deviceId == null}
                  onPress={handleSettingsPress}
                />
              </Controls.IconButtonWrapper>
            </Controls.Actions>
          </Controls.Header>

          {(() => {
            if (
              components != null &&
              selectedDevice?.available !== false &&
              selectedDevice?.ready !== false &&
              selectedDevice?.ready != null
            ) {
              // https://github.com/adobe/react-spectrum/issues/3104
              if (selectedTabKey == null) return;

              return (
                <Controls.Tabs
                  aria-label="Components"
                  selectedKey={selectedTabKey}
                  onSelectionChange={handleTabSelectionChange}
                  items={components.map((component) => {
                    return {
                      key: component.id,
                      textValue: '-',
                      context: {
                        component,
                      },
                    };
                  })}
                  renderTab={() => {
                    return <ControlsComponentTab />;
                  }}
                  renderTabPanel={({ value }) => {
                    const component = value.context.component;
                    const Component = getControlComponent(component.id);

                    return (
                      <ControlsComponentTabPanel>
                        <Component
                          key={component.id}
                          deviceId={selectedDevice.id}
                          component={component}
                        />
                      </ControlsComponentTabPanel>
                    );
                  }}
                />
              );
            }

            if (
              selectedDevice != null &&
              (selectedDevice.available === false ||
                selectedDevice.ready === false ||
                selectedDevice.ready == null)
            ) {
              return (
                <ControlsMessage
                  type="warning"
                  title={i18n.messageFormatter('device.unavailable')}
                  message={selectedDevice.unavailableMessage}
                />
              );
            }

            return (
              selectedDevice && (
                <ControlsMessage
                  title={i18n.messageFormatter('device.noControls')}
                  message={i18n.messageFormatter('device.noControlsDesc')}
                />
              )
            );
          })()}
        </Controls.Root>
      </AnimatePresence>
    </>
  );
}

function useComponents({ device, logs, hasVideoStreaming }) {
  const { components, componentsStartAt } = useMemo(() => {
    if (device == null) {
      return {
        components: null,
        componentsStartAt: null,
      };
    }

    const components = [...(device.ui?.components ?? [])];

    const hasCamera =
      device.images?.some((image) => {
        return image.type === 'camera';
      }) ?? false;

    const hasVideo =
      hasVideoStreaming &&
      (device.videos?.some((video) => {
        return video.type === 'camera';
      }) ??
        false);

    const hasInsights =
      Object.values(logs ?? {}).some((log) => {
        return log.type === 'boolean';
      }) ?? false;

    // If device has video streaming or camera images, add a combined camera tab
    if (hasVideo || hasCamera) {
      components.unshift({ id: 'camera', capabilities: [] });
    }

    if (hasInsights) {
      components.push({ id: 'timeline', capabilities: [] });
    }

    return {
      // If the device has video/camera, that UI component should be the first one.
      componentsStartAt: hasVideo || hasCamera ? 0 : (device.ui?.componentsStartAt ?? 0),
      components: components.length > 0 ? components : null,
    };
  }, [device, logs, hasVideoStreaming]);

  return { components, componentsStartAt };
}

function getControlComponent(componentId) {
  switch (componentId) {
    case 'battery':
      return Battery;
    case 'button':
      return ButtonGroup;
    case 'color':
      return Color;
    case 'media':
      return Media;
    case 'camera':
      return Camera;
    case 'picker':
      return Picker;
    case 'sensor':
      return SensorGroup;
    case 'slider':
      return SliderAdapter;
    case 'ternary':
      return Ternary;
    case 'thermostat':
      return Thermostat;
    case 'timeline':
      return Timeline;
    case 'toggle':
      return Toggle;
    default:
      return Fragment;
  }
}

Controls.Root = styled(motion.div)`
  position: absolute;
  display: flex;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: stretch;
  background-color: transparent;
`;

Controls.Tabs = styled(Tabs)`
  flex: 1 1 auto;

  ${Tabs.TabList} {
    justify-content: center;
    padding: ${su(4)} 0;
  }
`;

Controls.Header = styled.div`
  display: flex;
  height: ${su(8)};
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
`;

Controls.Actions = styled.div`
  display: flex;
  width: ${su(8)};
  flex: 0 0 ${su(8)};
`;

Controls.IconButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 5px;
`;
